import React, { useState, useEffect, useCallback } from 'react';
import { Heart, TrendingUp, Hash, DollarSign, Download, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminTips = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tourLabels, setTourLabels] = useState({});
  const token = localStorage.getItem('adminToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchStats = useCallback(async () => {
    try {
      const [statsRes, toursRes] = await Promise.all([
        axios.get(`${API}/admin/tips/stats`, { headers }),
        axios.get(`${API}/tours`).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      const labels = { unknown: 'Sin tour asociado' };
      toursRes.data.forEach(t => { labels[t.id] = t.name?.es || t.name || t.id; });
      setTourLabels(labels);
    } catch (err) {
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const exportCSV = () => {
    if (!stats?.tips?.length) { toast.error('No hay datos para exportar'); return; }
    const rows = [['Fecha', 'Nombre', 'Importe (€)', 'Estado', 'Review ID']];
    stats.tips.forEach(t => {
      const date = t.created_at ? new Date(t.created_at).toLocaleDateString('es-ES') : '';
      rows.push([date, t.name || 'Anónimo', t.amount?.toFixed(2) || '0', t.payment_status, t.review_id || '']);
    });
    const csv = rows.map(r => r.join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `propinas_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exportado');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#C2410C]" />
      </div>
    );
  }

  const { total_amount = 0, total_count = 0, avg_amount = 0, by_tour = {}, monthly = [], tips = [] } = stats || {};

  return (
    <div className="p-6 lg:p-8 max-w-6xl" data-testid="admin-tips-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1C1917]">Propinas</h1>
          <p className="text-sm text-[#57534E] mt-1">Estadísticas y detalle de propinas recibidas</p>
        </div>
        <Button variant="outline" onClick={exportCSV} data-testid="tips-export-csv">
          <Download className="h-4 w-4 mr-2" /> Exportar CSV
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl p-5 border border-[#E7E5E4]" data-testid="tips-stat-total">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center">
              <DollarSign className="h-5 w-5 text-rose-600" />
            </div>
          </div>
          <p className="text-sm text-[#57534E]">Importe total</p>
          <p className="text-3xl font-bold text-[#1C1917]">{total_amount.toFixed(2)} &euro;</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-[#E7E5E4]" data-testid="tips-stat-count">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center">
              <Hash className="h-5 w-5 text-rose-600" />
            </div>
          </div>
          <p className="text-sm text-[#57534E]">Número de propinas</p>
          <p className="text-3xl font-bold text-[#1C1917]">{total_count}</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-[#E7E5E4]" data-testid="tips-stat-avg">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-rose-600" />
            </div>
          </div>
          <p className="text-sm text-[#57534E]">Propina media</p>
          <p className="text-3xl font-bold text-[#1C1917]">{avg_amount.toFixed(2)} &euro;</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-[#E7E5E4]" data-testid="tips-stat-heart">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center">
              <Heart className="h-5 w-5 text-rose-600" />
            </div>
          </div>
          <p className="text-sm text-[#57534E]">Tasa de propina</p>
          <p className="text-3xl font-bold text-[#1C1917]">{total_count > 0 ? '100%' : '0%'}</p>
          <p className="text-xs text-[#78716C]">de los que eligen dejar propina</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* By Tour */}
        <div className="bg-white rounded-xl p-5 border border-[#E7E5E4]" data-testid="tips-by-tour">
          <h3 className="font-semibold text-[#1C1917] mb-4">Propinas por tour</h3>
          {Object.keys(by_tour).length === 0 ? (
            <p className="text-sm text-[#78716C] py-4 text-center">Sin datos aún</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(by_tour).map(([tourId, data]) => {
                const maxTotal = Math.max(...Object.values(by_tour).map(d => d.total), 1);
                const pct = (data.total / maxTotal) * 100;
                return (
                  <div key={tourId}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[#1C1917] font-medium truncate mr-2">
                        {tourLabels[tourId] || tourId}
                      </span>
                      <span className="text-[#57534E] flex-shrink-0">
                        {data.total.toFixed(2)} &euro; ({data.count})
                      </span>
                    </div>
                    <div className="w-full bg-[#F5F3EF] rounded-full h-2">
                      <div
                        className="bg-rose-500 h-2 rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Monthly Evolution */}
        <div className="bg-white rounded-xl p-5 border border-[#E7E5E4]" data-testid="tips-monthly">
          <h3 className="font-semibold text-[#1C1917] mb-4">Evolución mensual</h3>
          {monthly.length === 0 ? (
            <p className="text-sm text-[#78716C] py-4 text-center">Sin datos aún</p>
          ) : (
            <div className="space-y-3">
              {monthly.slice(-12).map((m) => {
                const maxTotal = Math.max(...monthly.map(d => d.total), 1);
                const pct = (m.total / maxTotal) * 100;
                const [year, mon] = m.month.split('-');
                const monthName = new Date(parseInt(year), parseInt(mon) - 1).toLocaleDateString('es-ES', { month: 'short', year: 'numeric' });
                return (
                  <div key={m.month}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[#1C1917] font-medium capitalize">{monthName}</span>
                      <span className="text-[#57534E]">
                        {m.total.toFixed(2)} &euro; ({m.count})
                      </span>
                    </div>
                    <div className="w-full bg-[#F5F3EF] rounded-full h-2">
                      <div
                        className="bg-rose-500 h-2 rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Recent Tips Table */}
      <div className="bg-white rounded-xl border border-[#E7E5E4] overflow-hidden" data-testid="tips-table">
        <div className="p-5 border-b border-[#E7E5E4]">
          <h3 className="font-semibold text-[#1C1917]">Historial de propinas</h3>
        </div>
        {tips.length === 0 ? (
          <div className="py-12 text-center">
            <Heart className="h-10 w-10 mx-auto text-[#D6D3D1] mb-3" />
            <p className="text-[#78716C]">No hay propinas registradas aún</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#FAFAF9] text-[#57534E]">
                  <th className="text-left px-5 py-3 font-medium">Fecha</th>
                  <th className="text-left px-5 py-3 font-medium">Nombre</th>
                  <th className="text-right px-5 py-3 font-medium">Importe</th>
                  <th className="text-left px-5 py-3 font-medium">Mensaje</th>
                  <th className="text-center px-5 py-3 font-medium">Estado</th>
                </tr>
              </thead>
              <tbody>
                {tips.slice(0, 50).map((tip) => (
                  <tr key={tip.id} className="border-t border-[#F5F5F4] hover:bg-[#FAFAF9]">
                    <td className="px-5 py-3 text-[#1C1917]">
                      {tip.created_at ? new Date(tip.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                    </td>
                    <td className="px-5 py-3 text-[#1C1917] font-medium">{tip.name || 'Anónimo'}</td>
                    <td className="px-5 py-3 text-right font-semibold text-rose-600">{tip.amount?.toFixed(2)} &euro;</td>
                    <td className="px-5 py-3 text-[#78716C] max-w-[200px] truncate">{tip.message || '-'}</td>
                    <td className="px-5 py-3 text-center">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        tip.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {tip.payment_status === 'paid' ? 'Pagada' : 'Pendiente'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTips;
